// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * What a space event leaves on the globe when its object is no longer in the
 * GP mirror (a re-entry): the orbit it last flew, drawn as a ring (headset)
 * and a ground track ending where it came down (globes), plus a small ring
 * and a pin at that spot. The VPS sends the last GP record and, when it knows
 * them, the time and place (`SatelliteEvent.past`); this turns them into the
 * same paths the live objects draw, so both renderers need nothing new but a
 * line list.
 *
 * Paths are Earth-fixed km, as `engine.orbitPath` writes them. SGP4 is the
 * plain-JS build, loaded when the first event is opened (the worker has no
 * part in it: one object, 482 points).
 */
import type { PastObject, SatelliteOmm } from './satelliteTypes'
import { EARTH_RADIUS_KM, GLOBE_RADIUS_KM, orbitShape, type OrbitShape } from './satelliteOrbit'

/** Where the object came down, and how sure that is. */
export interface PastPlace {
  lat: number
  lon: number
  /** A prediction, or worked out here from the last orbit: draw and say it as "about". */
  approximate: boolean
  /** 'given' (the VPS), 'estimated' (this module), nothing else. */
  from: 'given' | 'estimated'
}

export interface PastView {
  shape: OrbitShape
  /** The last record's epoch, ms. */
  epochMs: number
  /** The moment the paths are drawn for, ms (when it came down, else the epoch). */
  at: number
  /** One period as a closed ring, Earth-fixed as of `at`. */
  ellipse: Float32Array
  /** Ground track, ending at `at` when that is when it came down. */
  track: Float32Array
  place: PastPlace | null
  /** Pairs of points (a ring on the ground and a pin up from it), Earth-fixed km. */
  marks: Float32Array
}

/** A time with a time of day in it; a bare date ("2026-10-23") gives NaN. */
export function parseMoment(text: string | undefined): number {
  if (!text || !/T\d\d:\d\d/.test(text)) return NaN
  return Date.parse(/(Z|[+-]\d\d:?\d\d)$/.test(text) ? text : `${text}Z`)
}

/** Seconds a GP record's EPOCH (written without a zone, in UTC) says. */
export function epochMs(omm: Pick<SatelliteOmm, 'EPOCH'>): number {
  return Date.parse(/(Z|[+-]\d\d:?\d\d)$/.test(omm.EPOCH) ? omm.EPOCH : `${omm.EPOCH}Z`)
}

/** Ring of `RING_SEGMENTS` chords and a pin, around a point on the ground. */
const RING_SEGMENTS = 28
const RING_RADIUS_KM = 220
const PIN_KM = 140

/** The ring and pin at a place, as pairs of Earth-fixed points. */
export function placeMarks(lat: number, lon: number): Float32Array {
  const la = (lat * Math.PI) / 180
  const lo = (lon * Math.PI) / 180
  const n = [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)]
  const east = [-Math.sin(lo), Math.cos(lo), 0]
  const north = [-Math.sin(la) * Math.cos(lo), -Math.sin(la) * Math.sin(lo), Math.cos(la)]
  const rho = RING_RADIUS_KM / GLOBE_RADIUS_KM
  const ring: number[][] = []
  for (let s = 0; s <= RING_SEGMENTS; s++) {
    const a = (s / RING_SEGMENTS) * Math.PI * 2
    const c = Math.cos(a)
    const d = Math.sin(a)
    ring.push([0, 1, 2].map((k) => GLOBE_RADIUS_KM * (Math.cos(rho) * n[k] + Math.sin(rho) * (c * east[k] + d * north[k]))))
  }
  const out: number[] = []
  for (let s = 1; s < ring.length; s++) out.push(...ring[s - 1], ...ring[s])
  out.push(...n.map((v) => v * GLOBE_RADIUS_KM), ...n.map((v) => v * (GLOBE_RADIUS_KM + PIN_KM)))
  return new Float32Array(out)
}

/** The orbit's paths and the place, or null when the elements cannot be read. */
export async function buildPastView(past: PastObject): Promise<PastView | null> {
  const omm = past.elements
  if (!omm || !Number.isFinite(omm.MEAN_MOTION) || omm.MEAN_MOTION <= 0) return null
  const epoch = epochMs(omm)
  if (!Number.isFinite(epoch)) return null
  const [{ json2satrec }, { gstime, propagate }, { eciToEcf }] = await Promise.all([
    import('satellite.js-sgp4/io.js'),
    import('satellite.js-sgp4/propagation.js'),
    import('satellite.js-sgp4/transforms.js'),
  ])
  const rec = json2satrec(omm as unknown as Parameters<typeof json2satrec>[0])
  const shape = orbitShape(omm)
  const periodMs = shape.periodMin * 60_000
  const decayMs = parseMoment(past.decayAt)
  const at = Number.isFinite(decayMs) ? decayMs : epoch

  /** Earth-fixed km at `ms`, turned with the Earth of `frameMs`; null once SGP4 has no answer. */
  const ecf = (ms: number, frameMs: number): [number, number, number] | null => {
    const pv = propagate(rec, new Date(ms))
    const p = pv?.position
    if (!p || !Number.isFinite(p.x)) return null
    const e = eciToEcf(p, gstime(new Date(frameMs)))
    // Under the ground the orbit has ended.
    return Math.hypot(e.x, e.y, e.z) < EARTH_RADIUS_KM - 20 ? null : [e.x, e.y, e.z]
  }
  const path = (start: number, end: number, fixed: boolean): Float32Array => {
    const samples = 241
    const out = new Float32Array(samples * 3)
    for (let s = 0; s < samples; s++) {
      const ms = start + ((end - start) * s) / (samples - 1)
      const v = ecf(ms, fixed ? at : ms)
      out.set(v ?? [NaN, NaN, NaN], s * 3)
    }
    return out
  }
  // The ring is the orbit as it was flown, from the record's own epoch when the
  // moment it ended is far past it (SGP4 has no drag left to trust days later).
  const ringFrom = Math.abs(at - epoch) > 3 * 86_400_000 ? epoch : at
  const ellipse = path(ringFrom, ringFrom + periodMs, true)
  const track = Number.isFinite(decayMs) ? path(at - periodMs, at, false) : path(at - periodMs / 2, at + periodMs / 2, false)

  let place: PastPlace | null = null
  const given = past.place
  if (given && Number.isFinite(given.lat) && Number.isFinite(given.lon) && Math.abs(given.lat) <= 90 && Math.abs(given.lon) <= 180) {
    place = { lat: given.lat, lon: given.lon, approximate: given.approximate ?? false, from: 'given' }
  } else if (Number.isFinite(decayMs) && Math.abs(decayMs - epoch) <= 3 * 86_400_000) {
    // Within days of the last record SGP4 still tells roughly where it was.
    const v = ecf(decayMs, decayMs) ?? lastFinite(track)
    if (v) {
      const r = Math.hypot(v[0], v[1], v[2])
      place = {
        lat: (Math.asin(v[2] / r) * 180) / Math.PI,
        lon: (Math.atan2(v[1], v[0]) * 180) / Math.PI,
        approximate: true,
        from: 'estimated',
      }
    }
  }
  return { shape, epochMs: epoch, at, ellipse, track, place, marks: place ? placeMarks(place.lat, place.lon) : new Float32Array(0) }
}

function lastFinite(pts: Float32Array): [number, number, number] | null {
  for (let o = pts.length - 3; o >= 0; o -= 3) if (pts[o] === pts[o]) return [pts[o], pts[o + 1], pts[o + 2]]
  return null
}
